/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* eslint-disable no-restricted-globals */

/**
 * Possible meta data to be provides to an error
 */
export type Extensions = Readonly<Record<string, Readonly<Gaia.Jsonifiable>>> &
  Partial<KnownExtensions>

/**
 * Possible meta data to be provides to an error
 */
type KnownExtensions = Readonly<{
  code: string
  statusCode: number
  publicMsg: string
}>

/**
 * Loose shape of error types that we know about
 */
export type KnownError = (BaseError | Error) & { toJSON?: () => any }

/**
 * Known error codes
 */
export enum ErrorCode {
  ERROR = 'ERROR',
  BAD_USER_INPUT = 'BAD_USER_INPUT',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  SERVICE_ERROR = 'SERVICE_ERROR',
  ASSERT_ERROR = 'ASSERT_ERROR',
}

/**
 * Default public error messages
 */
export enum PublicErrorMsg {
  ERROR = 'Oops! Something went wrong',
  SERVICE_ERROR = 'Oops! Something went wrong. Please try again, and contact support if the issue persists.',
  BAD_USER_INPUT = 'We were unable to process the information you provided.',
  UNAUTHENTICATED = 'You must be authenticated to perform this action.',
  FORBIDDEN = `You don't have the right permissions to perform this action.`,
  NOT_FOUND = `We couldn't find what you were looking for.`,
  OPEN_BANKING_SERVICE_ERROR = `We had an issue checking if you have already connected your bank account. Please try again soon.`,
}

/**
 * Allow for patching standard Error objects with a string code
 */
declare global {
  interface Error {
    code?: string
    statusCode?: number
    publicMsg?: string
  }
}

/**
 * Common structuring of an error object, which aims to be compatible
 * with Apollo
 */
export class BaseError extends Error {
  /**
   * Informative, non-non detail leaking code
   */
  override readonly code: string | ErrorCode = ErrorCode.ERROR

  /**
   * If this error is transported over http then this is the http
   * response status code we should use
   */
  override readonly statusCode: number = 500

  /**
   * If an error ever makes it into public domain this is what should be
   * used/serialised
   */
  override readonly publicMsg: string | PublicErrorMsg = PublicErrorMsg.ERROR

  /**
   * When an error is thrown we can attach arbitrary meta data for later
   * processing
   */
  protected readonly ext: Extensions

  constructor(code?: string, extensions: Extensions = {}) {
    super(code ?? extensions.code)
    this.ext = code ? { code, ...extensions } : extensions

    if (typeof extensions.statusCode === 'number') {
      this.statusCode = extensions.statusCode
    }

    if (typeof extensions.publicMsg === 'string') {
      this.publicMsg = extensions.publicMsg
    }
  }

  /**
   * Get a complete list of extensions
   */
  get extensions(): Extensions {
    return {
      ...this.ext,
      code: this.code,
      statusCode: this.statusCode,
      publicMsg: this.publicMsg,
    }
  }
}

export { BaseError as Error }

/**
 * A user has [been allowed to] incorrectly attempt some input action -
 * mirrors Apollo.BAD_USER_INPUT
 */
export class InputError extends BaseError {
  override statusCode = this.ext.statusCode ?? 400
  override code = this.ext.code ?? ErrorCode.BAD_USER_INPUT
  override publicMsg = this.ext.publicMsg ?? PublicErrorMsg.BAD_USER_INPUT
}

/**
 * An action is insufficiently authenticated - mirrors
 * Apollo.UNAUTHENTICATED
 */
export class AuthError extends BaseError {
  override statusCode = this.ext.statusCode ?? 401
  override code = this.ext.code ?? ErrorCode.UNAUTHENTICATED
  override publicMsg = this.ext.publicMsg ?? PublicErrorMsg.UNAUTHENTICATED
}

/**
 * An action has insufficient authorization - mirrors Apollo.FORBIDDEN
 */
export class ForbiddenError extends BaseError {
  override statusCode = this.ext.statusCode ?? 403
  override code = this.ext.code ?? ErrorCode.FORBIDDEN
  override publicMsg = this.ext.publicMsg ?? PublicErrorMsg.FORBIDDEN
}

/**
 * An object cannot be resolved given the input
 */
export class NotFoundError extends BaseError {
  override statusCode = this.ext.statusCode ?? 404
  override code = this.ext.code ?? ErrorCode.NOT_FOUND
  override publicMsg = this.ext.publicMsg ?? PublicErrorMsg.NOT_FOUND
}

/**
 * A request was unresolvable due to some service error
 */
export class ServiceError extends BaseError {
  override statusCode = this.ext.statusCode ?? 500
  override code = this.ext.code ?? ErrorCode.SERVICE_ERROR
  override publicMsg = this.ext.publicMsg ?? PublicErrorMsg.SERVICE_ERROR
}

/**
 * An assertion has failed
 */
export class AssertError extends BaseError {
  override statusCode = this.ext.statusCode ?? 500
  override code = this.ext.code ?? ErrorCode.ASSERT_ERROR
  override publicMsg = this.ext.publicMsg ?? PublicErrorMsg.SERVICE_ERROR
}
